import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Box, 
  IconButton, 
  Typography,
  Link,
  CircularProgress,
  Divider,
  Chip,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchChatCompletion } from '../utils/api';
import { API_CONFIG } from '../config/constants';
import Logo from './Logo';

// Cache for AI notes responses by profileId
const aiNotesCache = new Map();

// Helper function to parse individual elements from complex query strings
function parseQueryElements(queryString) {
  if (!queryString) return [];
  
  // Remove outer parentheses if present
  let cleanQuery = queryString.trim();
  if (cleanQuery.startsWith('(') && cleanQuery.endsWith(')')) {
    cleanQuery = cleanQuery.slice(1, -1);
  }
  
  // Split by OR and AND, then clean up each element
  const elements = cleanQuery
    .split(/\s+(?:OR|AND)\s+/)
    .map(element => element.trim())
    .filter(element => element.length > 0)
    .map(element => {
      // Remove quotes and extra parentheses
      return element.replace(/^['"`(]+|['"`)]+$/g, '').trim();
    })
    .filter(element => element.length > 0);
  
  return elements;
}

// Helper function to get readable name for an element
function getReadableName(element, queries) {
  // If it's a hex ID (24 characters, alphanumeric), try to look it up
  if (/^[a-f0-9]{24}$/i.test(element) && queries && queries[element]) {
    return queries[element].name || element;
  }
  
  // If it contains a colon, it might be a field:value query - extract the value part
  if (element.includes(':')) {
    const parts = element.split(':');
    if (parts.length > 1) {
      let value = parts[parts.length - 1].trim();
      // Clean up the value
      value = value.replace(/^['"`(]+|['"`)]+$/g, '');
      return value;
    }
  }
  
  // Return as-is if it's already readable
  return element;
}

// Helper function to get matching criteria for a profile
function getMatchingCriteria(profileId, macroMappingData) {
  if (!macroMappingData || !macroMappingData.queryResults || !macroMappingData.autoMacroMap) {
    return [];
  }

  // Find the query result for this profile
  const queryResult = macroMappingData.queryResults.find(result => result.profile_id === profileId);
  if (!queryResult || !queryResult.macro_ids) {
    return [];
  }

  // Process each macro_id to extract readable criteria
  const allCriteria = [];
  
  queryResult.macro_ids.forEach(macroId => {
    const macroValue = macroMappingData.autoMacroMap[macroId];
    if (!macroValue) return;

    // Parse the macro value to extract individual elements
    const elements = parseQueryElements(macroValue);
    
    elements.forEach(element => {
      const readableName = getReadableName(element, macroMappingData.queries);
      
      // Only add if it's meaningful (not just hex IDs or technical terms)
      if (readableName && 
          readableName.length > 2 && 
          !readableName.startsWith('profile_type:') &&
          !/^[a-f0-9]{24}$/i.test(readableName)) {
        allCriteria.push(readableName);
      }
    });
  });

  // Remove duplicates and return
  return [...new Set(allCriteria)];
}

// Helper function to create a summary of candidate profile for AI prompt
function createCandidateProfileSummary(candidate) {
  if (!candidate) return 'No candidate data available';
  
  const name = candidate?.candidate?.full_name ? unescape(candidate.candidate.full_name) : 'Unknown';
  
  // Get current work experience
  const currentWork = candidate?.workexp?.find(exp => exp.duration?.to_present);
  const title = currentWork?.projects?.map(proj => proj.role_and_group?.title).filter(Boolean)[0] || 'Unknown title';
  const company = currentWork?.company?.name || 'Unknown company';
  
  // Get location
  const location = candidate?.candidate?.location;
  const locationStr = location ? `${location.city || ''}, ${location.state || ''}, ${location.country || ''}`.replace(/^,\s*|,\s*$/g, '') : 'Unknown location';
  
  // Get skills
  const skills = candidate?.skills || [];
  
  // Get work experience summary
  const workExperience = candidate?.workexp?.map(exp => {
    const companyName = exp.company?.name || 'Unknown company';
    const roles = exp.projects?.map(proj => proj.role_and_group?.title).filter(Boolean) || [];
    const duration = exp.duration ? `${exp.duration.start_year || ''}-${exp.duration.to_present ? 'Present' : exp.duration.end_year || ''}` : '';
    return `${roles.join(', ')} at ${companyName} (${duration})`;
  }) || [];
  
  // Get education
  const education = candidate?.education?.map(edu => {
    const school = edu.school?.name || 'Unknown school';
    const degree = edu.degree || 'Unknown degree';
    const field = edu.field_of_study || '';
    return `${degree} ${field ? 'in ' + field : ''} from ${school}`;
  }) || [];
  
  return {
    name,
    title,
    company,
    location: locationStr,
    skills: skills.slice(0, 10), // Limit to top 10 skills
    workExperience: workExperience.slice(0, 5), // Limit to last 5 positions
    education: education.slice(0, 3) // Limit to top 3 education entries
  };
}

/**
 * Sliding sidebar component that displays candidate profile with AI-generated notes
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the sidebar is open
 * @param {Function} props.onClose - Function to close the sidebar
 * @param {Object} props.candidate - The candidate data
 * @param {string} props.profileId - The profile ID for the iframe URL
 * @param {Object} props.macroMappingData - The macro mapping data for matching criteria
 * @returns {JSX.Element} The rendered component
 */
const CandidateProfileSidebar = ({ 
  open, 
  onClose, 
  candidate, 
  profileId,
  macroMappingData 
}) => {
  const [aiNotes, setAiNotes] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Extract candidate name
  const candidateName = candidate?.candidate?.full_name 
    ? unescape(candidate.candidate.full_name) 
    : 'Candidate Profile';

  // Construct the iframe URL using the correct FindEm format
  const profileUrl = profileId 
    ? `https://app-next-main.findem.ai/profiles/${profileId}` 
    : 'https://app-next-main.findem.ai/profiles/default';

  // Get matching criteria
  const matchingCriteria = getMatchingCriteria(profileId, macroMappingData);

  // Generate AI notes when sidebar opens
  useEffect(() => {
    if (open && candidate && profileId && !aiNotes && !isLoadingNotes) {
      // Check cache first
      const cachedNotes = aiNotesCache.get(profileId);
      if (cachedNotes) {
        setAiNotes(cachedNotes);
      } else {
        generateAINotes();
      }
    }
  }, [open, candidate, profileId, matchingCriteria]);

  const downloadPDF = () => {
    // Create a new window with the analysis content
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Candidate Analysis - ${candidateName}</title>
          <style>
            body {
              font-family: 'Roboto', Arial, sans-serif;
              line-height: 1.6;
              margin: 40px;
              color: #333;
            }
            .header {
              border-bottom: 2px solid #6750A4;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .candidate-name {
              font-size: 24px;
              font-weight: 600;
              color: #333;
              margin-bottom: 10px;
            }
            .section-title {
              font-size: 18px;
              font-weight: 600;
              color: #333;
              margin: 20px 0 10px 0;
            }
            .criteria-list {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 20px;
            }
            .criteria-item {
              background-color: #f0f7ff;
              border: 1px solid #6750A4;
              border-radius: 16px;
              padding: 4px 12px;
              font-size: 12px;
              color: #6750A4;
            }
            .analysis-content {
              white-space: pre-wrap;
              line-height: 1.8;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e0e0e0;
              font-size: 12px;
              color: #666;
              text-align: center;
            }
            @media print {
              body { margin: 20px; }
              .header { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="candidate-name">${candidateName}</div>
            <div style="color: #666; font-size: 14px;">AI Candidate Analysis Report</div>
          </div>
          
          ${matchingCriteria.length > 0 ? `
            <div class="section-title">Matching Criteria</div>
            <div class="criteria-list">
              ${matchingCriteria.map(criterion => `<span class="criteria-item">${criterion}</span>`).join('')}
            </div>
          ` : ''}
          
          <div class="section-title">Analysis</div>
          <div class="analysis-content">${aiNotes || 'No analysis available'}</div>
          
          <div class="footer">
            Generated on ${new Date().toLocaleDateString()} by IntakeMate AI Analysis
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load then trigger print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const generateAINotes = async () => {
    setIsLoadingNotes(true);
    setNotesError(null);
    
    try {
      // Create candidate profile summary
      const profileSummary = createCandidateProfileSummary(candidate);
      
      // Create the prompt for AI
      const userInput = `Provide a concise candidate analysis focusing on key qualifications and fit.

CANDIDATE: ${profileSummary.name}
ROLE: ${profileSummary.title} at ${profileSummary.company}
LOCATION: ${profileSummary.location}
TOP SKILLS: ${profileSummary.skills.slice(0, 5).join(', ')}

RECENT EXPERIENCE:
${profileSummary.workExperience.slice(0, 2).map(exp => `• ${exp}`).join('\n')}

MATCHING CRITERIA:
${matchingCriteria.length > 0 ? matchingCriteria.slice(0, 4).map(criteria => `• ${criteria}`).join('\n') : 'General fit assessment'}

Please provide a brief analysis (3-4 paragraphs max) covering:
• Key strengths and relevant experience
• Alignment with criteria and role requirements  
• Overall assessment and recommendation

Keep it concise, actionable, and recruiter-focused.`;

      const agentInput = 'You are a professional recruiter. Provide concise, actionable candidate insights in 3-4 short paragraphs maximum. Focus on practical hiring decisions.';

      // Call the chat completion API
      const response = await fetchChatCompletion(API_CONFIG.url, userInput, agentInput);
      
      if (response && typeof response === 'string') {
        setAiNotes(response);
        // Cache the response for this profileId
        aiNotesCache.set(profileId, response);
      } else {
        setNotesError('Failed to generate candidate analysis');
      }
    } catch (error) {
      console.error('Error generating AI notes:', error);
      setNotesError('Error generating candidate analysis. Please try again.');
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Reset notes when sidebar closes
  useEffect(() => {
    if (!open) {
      setAiNotes('');
      setNotesError(null);
      setActiveTab(0); // Reset to first tab
    }
  }, [open]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '60%',
          minWidth: '600px',
          maxWidth: '800px',
          height: '100vh',
          backgroundColor: '#ffffff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              {candidateName}
            </Typography>
            <Link
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#6750A4',
                textDecoration: 'underline',
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                '&:hover': {
                  color: '#5a3d99',
                  textDecoration: 'underline',
                },
              }}
            >
              View Full Profile
              <OpenInNewIcon sx={{ fontSize: '1rem' }} />
            </Link>
            <Box sx={{ marginLeft: 'auto' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadPDF}
                disabled={!aiNotes}
                sx={{
                  color: '#6750A4',
                  borderColor: '#6750A4',
                  fontSize: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f0f7ff',
                    borderColor: '#5a3d99',
                  },
                  '&:disabled': {
                    color: '#ccc',
                    borderColor: '#ccc',
                  },
                }}
              >
                Download PDF
              </Button>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            aria-label="candidate profile tabs"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#6750A4',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                color: '#666',
                '&.Mui-selected': {
                  color: '#6750A4',
                  fontWeight: 600,
                },
              },
            }}
          >
            <Tab label="Full Profile" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label="AI Analysis" id="tab-1" aria-controls="tabpanel-1" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Full Profile Tab */}
          {activeTab === 0 && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {profileId ? (
                <iframe
                  src={profileUrl}
                  title={`${candidateName} - Full Profile`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flexGrow: 1,
                  }}
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Profile URL not available
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* AI Analysis Tab */}
          {activeTab === 1 && (
            <Box
            sx={{
              padding: '20px',
              backgroundColor: '#fafafa',
              flex: 1,
              overflow: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
              AI Candidate Analysis
            </Typography>
            
            {/* Matching Criteria */}
            {matchingCriteria.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666', mb: 1 }}>
                  Matching Criteria:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {matchingCriteria.slice(0, 6).map((criterion, index) => (
                    <Chip
                      key={index}
                      label={criterion}
                      size="small"
                      icon={<CheckCircleIcon />}
                      color="success"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: '22px',
                        maxWidth: '200px',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiChip-icon': {
                          fontSize: '0.8rem',
                          width: '14px',
                          height: '14px',
                        }
                      }}
                    />
                  ))}
                  {matchingCriteria.length > 6 && (
                    <Chip
                      label={`+${matchingCriteria.length - 6} more`}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: '22px'
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* AI Notes Content */}
            {isLoadingNotes ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Generating candidate analysis...
                </Typography>
              </Box>
            ) : notesError ? (
              <Box>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {notesError}
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={generateAINotes}
                  sx={{ color: '#6750A4' }}
                >
                  Try again
                </Link>
              </Box>
            ) : aiNotes ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#333',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {aiNotes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click to generate AI analysis of this candidate
              </Typography>
            )}

            {!isLoadingNotes && !aiNotes && !notesError && (
              <Link
                component="button"
                variant="body2"
                onClick={generateAINotes}
                sx={{ color: '#6750A4', mt: 1 }}
              >
                Generate Analysis
              </Link>
            )}
            </Box>
          )}

        </Box>
      </Box>
    </Drawer>
  );
};

export default CandidateProfileSidebar;
